import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Repeat, ShieldCheck, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { payEducatorSubscription } from "@/lib/franchisePay";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function useEducatorPlan() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/educator/subscription").then(({ data }) => setD(data)), []);
  useEffect(() => { load(); }, [load]);
  return [d, load];
}

function PlanCard({ plan, onSubscribe, busy }) {
  const perks = ["Publish and sell unlimited courses", `Keep ${100 - plan.platform_percent}% of every course sale — VIRIKS platform fee only ${plan.platform_percent}%`, "Live classes, tests and course builder", "Earnings ledger with payouts to your verified bank"];
  return (
    <div className="bg-white border border-border rounded-2xl p-8 max-w-xl" data-testid="edu-plan-card">
      <div className="text-sm font-semibold text-amber">VIRIKS Educator Plan</div>
      <div className="flex items-end gap-2 mt-2"><span className="font-heading text-5xl font-black text-navy">₹{plan.price}</span><span className="text-muted-foreground mb-2">/ month + {plan.gst_percent}% GST</span></div>
      <div className="text-sm mt-2 space-y-1" data-testid="edu-plan-breakdown">
        <div className="flex justify-between"><span>Plan fee</span><span>{inr(plan.price)}</span></div>
        <div className="flex justify-between"><span>GST ({plan.gst_percent}%)</span><span>{inr(plan.gst)}</span></div>
        <div className="flex justify-between font-bold text-navy border-t border-border pt-2"><span>Charged monthly</span><span data-testid="edu-plan-total">{inr(plan.total)}</span></div>
      </div>
      <ul className="mt-6 space-y-2">{perks.map((p) => <li key={p} className="flex gap-2 text-sm"><CheckCircle2 size={17} className="text-amber shrink-0" />{p}</li>)}</ul>
      <Button disabled={busy} onClick={onSubscribe} className="mt-7 w-full h-12 rounded-full viriks-amber text-white font-semibold" data-testid="edu-subscribe"><Repeat size={16} className="mr-2" /> {busy ? "Please wait..." : "Set up monthly auto-debit"}</Button>
      <p className="text-xs text-muted-foreground mt-3">Approve an e-NACH bank mandate, UPI AutoPay or card once through Razorpay. {inr(plan.total)} is then debited every month until you cancel.</p>
    </div>
  );
}

export default function EducatorSubscription() {
  const [d, load] = useEducatorPlan();
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  if (!d) return <Loading rows={3} />;
  const s = d.subscription;
  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/educator/subscription");
      const res = await payEducatorSubscription(data, { name: user.name, email: user.email, contact: user.mobile });
      if (res?.active) toast.success("Your educator plan is active");
      else if (res) toast.info("Mandate set up. Activation will complete once Razorpay confirms the first payment.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const cancel = async () => {
    if (!window.confirm("Cancel auto-debit? Your courses stop selling once the current paid period (plus grace days) ends.")) return;
    try { await api.post("/educator/subscription/cancel"); toast.success("Auto-debit cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="educator-subscription">
      <PageHeader title="Educator Plan" subtitle="A monthly plan lets you publish and sell courses on VIRIKS" />
      {d.active ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 max-w-xl mb-6" data-testid="edu-plan-active">
          <div className="flex items-center gap-2 font-heading text-xl font-bold text-emerald-700"><ShieldCheck size={22} /> Plan active</div>
          <p className="text-sm mt-2">Paid until <b>{date(s?.paid_until)}</b> • {s?.paid_count || 0} payment(s) • status: {s?.status}</p>
          {s?.status === "cancelled" || s?.cancelled_at
            ? <p className="text-sm mt-2 text-muted-foreground">Auto-debit cancelled. Your courses keep selling until {date(s?.paid_until)} plus {d.plan.grace_days} grace days.</p>
            : <Button variant="outline" className="mt-4" onClick={cancel} data-testid="edu-cancel">Cancel auto-debit</Button>}
        </div>
      ) : (
        <>
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 max-w-xl mb-6 text-sm flex gap-2" data-testid="edu-plan-inactive">
            <XCircle size={18} className="text-yellow-700 shrink-0" />
            <span>{s ? `Your plan is not active (status: ${s.status}). Your courses are not on sale.` : "Activate your plan to create courses and start selling."} Students who already bought keep their access.</span>
          </div>
          <PlanCard plan={d.plan} onSubscribe={subscribe} busy={busy} />
        </>
      )}
    </div>
  );
}

export function PlanGate({ children }) {
  const { user } = useAuth();
  const [d] = useEducatorPlan();
  if (user?.role !== "educator") return children;
  if (!d) return <Loading rows={3} />;
  return d.active ? children : <EducatorSubscription />;
}
